import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Clock, AlertTriangle } from "lucide-react";
import { statusLabel, STATUS_COLORS, money, when, pretty } from "./claimsShared";

const TABS = [
  { key: "my", label: "My Claims" },
  { key: "to_review", label: "Check Claims" },
  { key: "awaiting_final", label: "Waiting for Admin" },
  { key: "more_info", label: "Need More Proof" },
  { key: "cn_pending", label: "Make CN" },
  { key: "closed", label: "Closed" },
];

export default function ClaimsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions || [];
  const isAdmin = (user?.roles || []).some((r) => ["Admin", "admin"].includes(r));
  const canCreate = isAdmin || perms.includes("claims.create");

  const [tab, setTab] = useState("my");
  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState({});
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ tab });
    if (search) p.set("search", search);
    api.get(`/claims?${p}`).then((r) => setClaims(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load claims"))
      .finally(() => setLoading(false));
    api.get("/claims/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="claims-list-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900">Claims Desk</h1>
        {canCreate && (
          <Button size="sm" onClick={() => navigate("/claims/new")} data-testid="new-claim-btn">
            <Plus className="w-4 h-4 mr-1" /> Raise Claim
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="claims-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${tab === t.key ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            data-testid={`claims-tab-${t.key}`}>
            {t.label} ({counts[t.key] ?? 0})
          </button>
        ))}
      </div>

      <div className="relative max-w-xs">
        <Search className="absolute left-2 top-2 w-4 h-4 text-zinc-400" />
        <Input className="pl-8 h-8 text-xs" placeholder="Search claim, customer or order..."
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="claims-search" />
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>
              {["Claim", "Customer", "Channel", "Type", "Claimed", "Approved", "Status", "Flags", "SLA", "Raised by", "Updated"].map((h) => (
                <th key={h} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-400" data-testid="claims-loading">Loading…</td></tr>)}
            {!loading && claims.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-6 text-center text-zinc-400" data-testid="claims-empty">No claims here yet.</td></tr>
            )}
            {claims.map((c) => (
              <tr key={c._id} onClick={() => navigate(`/claims/${c._id}`)}
                className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                data-testid={`claim-row-${c.claim_id}`}>
                <td className="px-3 py-2 font-mono font-semibold text-zinc-900">{c.claim_id}</td>
                <td className="px-3 py-2">{c.customer_name}</td>
                <td className="px-3 py-2 text-zinc-500">{c.channel || "—"}</td>
                <td className="px-3 py-2">{pretty(c.claim_type)}</td>
                <td className="px-3 py-2 font-medium">{money(c.claimed_value)}</td>
                <td className="px-3 py-2">{c.approved_value ? money(c.approved_value) : "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[c.status] || "bg-zinc-100 text-zinc-700"}`}
                    data-testid={`claim-status-${c.claim_id}`}>{statusLabel(c.status)}</span>
                </td>
                <td className="px-3 py-2 space-x-1 whitespace-nowrap">
                  {c.is_late && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid={`claim-late-badge-${c.claim_id}`}>Late</span>}
                  {c.evidence_late && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-semibold" data-testid={`claim-evidence-late-badge-${c.claim_id}`}>Evidence late</span>}
                  {c.email_not_sent && <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-semibold" data-testid={`claim-email-not-sent-badge-${c.claim_id}`}>Email not sent</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.sla_breached ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[10px] font-semibold" data-testid={`claim-sla-badge-${c.claim_id}`}>
                      <AlertTriangle className="w-3 h-3" /> SLA breached
                    </span>
                  ) : c.sla?.current_stage_due_at ? (
                    <span className="inline-flex items-center gap-1 text-zinc-500 text-[11px]">
                      <Clock className="w-3 h-3" /> {when(c.sla.current_stage_due_at)}
                    </span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2 text-zinc-500">{c.created_by_name}</td>
                <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{when(c.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
